import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import { useQuery } from "@tanstack/react-query";

//Services
import {
  JOBTITLES_QUERY_KEY,
  jobtitleService,
} from "../../../../../Services/jobtitlesService";
import {
  JOBLEVEL_QUERY_KEY,
  joblevelService,
} from "../../../../../Services/joblevelService";
import {
  companyjobTitleService,
  VIEW_COMPANY_JOBS_QUERY_KEY,
} from "../../../../../Services/companyjobtitleService";

//Types
import { JobTitles } from "../../../../../Types/MasterData/JobTitles";
import { JobLevel } from "../../../../../Types/MasterData/JobLevel";

const INITIAL_STATE = {
  company_code: "",
  job_title_id: 0,
  job_level_id: 0,
  description: "",
  min_salary: 0.0,
  max_salary: 0.0,
  status: -1,
  created_by: undefined,
  updated_by: undefined,
};

interface CreateModalProps {
  toggle?: () => void;
  company_code: string; // required
}

const CreateDepartmentForm: React.FC<CreateModalProps> = ({
  company_code,
  toggle,
}) => {
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);

  const createCompanyJobsMutation = useMutation({
    mutationFn: companyjobTitleService.createCompanyJobs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEW_COMPANY_JOBS_QUERY_KEY });
      toast.success("New Job has been created successfully.");
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Unable to create job. Please verify the entered information.",
        );
      }
    },
  });

  const { data: jobtitleData, isLoading: JobTitleisLoading } = useQuery<{
    data: JobTitles[];
  }>({
    queryKey: JOBTITLES_QUERY_KEY,
    queryFn: jobtitleService.getAllJobTitles,
  });

  const jobtitles: JobTitles[] =
    jobtitleData?.data?.filter((item) => item.status === 1) ?? [];

  const { data: joblevelData, isLoading: JobLevelisLoading } = useQuery<{
    data: JobLevel[];
  }>({
    queryKey: JOBLEVEL_QUERY_KEY,
    queryFn: joblevelService.getAllJobLevel,
  });

  const joblevels: JobLevel[] =
    joblevelData?.data?.filter((item) => item.status === 1) ?? [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSelectedJobTitle = (job_title_id: number) => {
    setFormData((prev) => ({ ...prev, job_title_id: job_title_id }));
  };

  const handleSelectedJobLevel = (job_level_id: number) => {
    setFormData((prev) => ({ ...prev, job_level_id: job_level_id }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    const { min_salary, max_salary } = formData;

    if ((min_salary && !max_salary) || (!min_salary && max_salary)) {
      toast.error(
        "Both Min Salary and Max Salary are required if one is filled.",
      );
      return;
    }

    if (min_salary && max_salary && Number(min_salary) > Number(max_salary)) {
      toast.error("Min Salary cannot be greater than Max Salary.");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const payload = {
      ...formData,
      created_by: user.id,
      updated_by: user.id,
      company_code: company_code,
    };
    createCompanyJobsMutation.mutate(payload);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Job Title<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.job_title_id}
              id="job_title_id"
              name="job_title_id"
              type="select"
              disabled={JobTitleisLoading}
              onChange={(e) => handleSelectedJobTitle(Number(e.target.value))}
            >
              <option value="0" disabled>
                {JobTitleisLoading ? "Loading..." : "Select Job Title"}
              </option>
              {!JobTitleisLoading &&
                jobtitles.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.job_title}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md="12">
            <Label>
              Job Level<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.job_level_id}
              id="job_level_id"
              name="job_level_id"
              type="select"
              disabled={JobLevelisLoading}
              onChange={(e) => handleSelectedJobLevel(Number(e.target.value))}
            >
              <option value="0" disabled>
                {JobLevelisLoading ? "Loading..." : "Select Job Level"}
              </option>
              {!JobLevelisLoading &&
                joblevels.map((item) => (
                  <option key={item.id} value={item.id}>
                    {`Lvl ${item.level_rank} - ${item.name}`}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md="6">
            <Label>Min Salary</Label>
            <Input
              id="min_salary"
              name="min_salary"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.min_salary ?? ""}
              onChange={handleInputChange}
              required={!!formData.max_salary}
            />
          </Col>
          <Col md="6">
            <Label>Max Salary</Label>
            <Input
              id="max_salary"
              name="max_salary"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.max_salary ?? ""}
              onChange={handleInputChange}
              required={!!formData.min_salary}
            />
          </Col>
          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createCompanyJobsMutation.isPending}
            >
              {createCompanyJobsMutation.isPending
                ? "Creating Job..."
                : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateDepartmentForm;
